import React, { useMemo, useState } from "react";
import { Table2, X } from "lucide-react";
import { formatValue } from "@/lib/chartTheme";
import { useA11y } from "@/context/A11yContext";

const MAX_ROWS = 200;

// Item 19: text alternative for every chart — a screen-reader summary + data table (always),
// plus a visible "Table" toggle when the user enabled "Show charts as data tables".
export function ChartA11y({ widget, data, type }) {
  const { prefs } = useA11y();
  const [open, setOpen] = useState(false);
  const columns = data?.columns || [];
  const rows = (data?.rows || []).slice(0, MAX_ROWS);
  const measMeta = useMemo(() => { const m = {}; (data?.meta?.measures || []).forEach((x) => { m[x.key] = x; }); return m; }, [data]);
  const dimMeta = useMemo(() => { const m = {}; (data?.meta?.dimensions || []).forEach((x) => { m[x.key] = x; }); return m; }, [data]);
  const labelFor = (c) => measMeta[c]?.label || dimMeta[c]?.label || String(c).replace(/_/g, " ");
  const summary = useMemo(() => {
    const title = widget?.title || `${type} chart`;
    if (!rows.length) return `${title}: no data.`;
    const meas = columns.filter((c) => measMeta[c] || rows.some((r) => typeof r[c] === "number"));
    const dims = columns.filter((c) => !meas.includes(c));
    const parts = [`${title}: ${type} chart with ${(data?.rows || []).length} data point${(data?.rows || []).length === 1 ? "" : "s"}`];
    if (dims.length) parts.push(`grouped by ${dims.map(labelFor).join(", ")}`);
    const m0 = meas[0];
    if (m0) {
      const vals = rows.map((r) => Number(r[m0])).filter(Number.isFinite);
      if (vals.length) {
        const hi = rows.reduce((a, r) => (Number(r[m0]) > Number(a[m0]) ? r : a), rows[0]);
        const lo = rows.reduce((a, r) => (Number(r[m0]) < Number(a[m0]) ? r : a), rows[0]);
        const key = dims[0];
        parts.push(`${labelFor(m0)} ranges from ${formatValue(lo[m0], measMeta[m0]?.format)}${key ? ` (${lo[key]})` : ""} to ${formatValue(hi[m0], measMeta[m0]?.format)}${key ? ` (${hi[key]})` : ""}`);
      }
    }
    return parts.join(", ") + ".";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, widget?.title, type]);
  if (!columns.length) return null;
  const table = (
    <table className="w-max min-w-full text-xs" aria-label={`${widget?.title || type} data`}>
      <thead className="sticky top-0 bg-[color:var(--surface-2)]"><tr>{columns.map((c) => <th key={c} scope="col" className="px-2 py-1 text-left font-medium text-[color:var(--ink-2)]">{labelFor(c)}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[color:var(--glass-border)]">{columns.map((c) => <td key={c} className={"px-2 py-1 text-[color:var(--ink-2)] " + (measMeta[c] ? "text-right tabular-nums" : "")}>{measMeta[c] ? formatValue(r[c], measMeta[c].format) : String(r[c] ?? "\u2014")}</td>)}</tr>)}</tbody>
    </table>
  );
  return (
    <>
      <div className="sr-only" data-testid="chart-a11y-summary" role="region" aria-label={`${widget?.title || type} text alternative`}>
        <p>{summary}</p>
        {!open && table}
      </div>
      {prefs.chart_tables && (
        <button data-testid="chart-a11y-toggle" type="button" onClick={() => setOpen((v) => !v)} aria-pressed={open} aria-label={open ? "Show chart" : "Show data table"}
          className="absolute bottom-1 right-1 z-20 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[color:var(--surface)]/90 border border-[color:var(--glass-border)] text-[10px] text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] shadow-sm">
          {open ? <X className="w-3 h-3" aria-hidden="true" /> : <Table2 className="w-3 h-3" aria-hidden="true" />}{open ? "Chart" : "Table"}
        </button>
      )}
      {open && (
        <div data-testid="chart-a11y-table" className="absolute inset-0 z-10 overflow-auto rounded-xl bg-[color:var(--glass-bg)] border border-[color:var(--glass-border)]">
          <p className="px-2 pt-1.5 pb-1 text-[11px] text-[color:var(--ink-3)]">{summary}</p>
          {table}
        </div>
      )}
    </>
  );
}
